import {
	Drawer,
	DrawerHeader,
	DrawerHeading,
	DrawerUnsavedChangesPrompt,
	I18nLocaleSwitcher,
	useDrawerUnsavedChangesPrompt,
	useDynamicConfig,
} from "@dgs/core";
import { Formik } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventTypeBaseForm } from "../eventTypes/EventTypeBaseForm";
import { useEventType } from "../eventTypes/EventTypeProvider";
import { EventTypeSettingsForm } from "../eventTypes/EventTypeSettingsForm";
import { EventTypeStepper } from "../eventTypes/EventTypeStepper";
import { eventTypeValidation } from "../eventTypes/eventTypeValidation";
import { EventTypeBaseFormValues, EventTypeTranslationEntry } from "../shared/types/eventType";

interface Props {
	open: boolean;
	handleClose: () => void;
}

const emptyTranslation: EventTypeTranslationEntry = {
	name: "",
};

const StyledDrawer = styled(Drawer)`
	width: 600px;
	max-width: 50vw;
`;

export const EventTypeCreateStepperDrawer: FC<Props> = ({ open, handleClose: handleCloseDrawer }) => {
	const { t } = useTranslation();
	const { currentCreateStep, clearBaseData } = useEventType();
	const { handleClose, onSubmitted, ...promptProps } = useDrawerUnsavedChangesPrompt(handleCloseDrawer);
	const { languageSettings } = useDynamicConfig();
	const { submitBaseData, baseData } = useEventType();
	const validate = eventTypeValidation(t);

	const initialValues: EventTypeBaseFormValues = useMemo(
		() => ({
			isDefault: false,
			isEventEditableByAllUsers: false,
			requiresParticipationApproval: false,
			showFeedbackEvaluation: false,
			eventRole: null,
			fallbackLanguage: null,
			color: null,
			translations: [{ locale: languageSettings.fallback, ...emptyTranslation }],
			image: null,
			feedback: [],
			allowedVisibilities: ["public", "logged_in_users", "my_project_team"],
		}),
		[languageSettings.fallback],
	);
	const UnsavedChangesPrompt = <DrawerUnsavedChangesPrompt {...promptProps} />;

	return (
		<StyledDrawer open={open} onClose={handleClose} onClosed={clearBaseData}>
			<Formik
				validate={validate}
				initialValues={baseData || initialValues}
				onSubmit={(values, formikHelpers) => submitBaseData(values, onSubmitted, formikHelpers)}
			>
				<>
					<DrawerHeader onClose={handleClose}>
						<DrawerHeading>{t("Create event type")}</DrawerHeading>
						{currentCreateStep === "base" && <I18nLocaleSwitcher emptyTranslation={emptyTranslation} />}
						<EventTypeStepper />
					</DrawerHeader>
					{currentCreateStep === "base" && (
						<EventTypeBaseForm unsavedChangesPrompt={UnsavedChangesPrompt} onSubmitted={onSubmitted} />
					)}
				</>
			</Formik>
			{currentCreateStep === "settings" && (
				<EventTypeSettingsForm
					unsavedChangesPrompt={UnsavedChangesPrompt}
					onSubmitted={onSubmitted}
					onCloseDrawer={handleClose}
				/>
			)}
		</StyledDrawer>
	);
};
